import { useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { uiPrompt } from '../utils/dialogs.js';

// Orders → Order forms (Joe, 5 Nov 2026): links PLD sends out so people can
// order (a big Christmas order, a staff order…). What they send lands on the
// Email inbox tab to review, filled in like an emailed order.

const btn = (primary) => ({
  padding: '7px 14px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: primary ? 'none' : '1px solid var(--border)', background: primary ? 'var(--pld-green)' : 'var(--surface)', color: primary ? '#fff' : 'var(--text)',
});
const input = { padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' };
const label = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 };

const linkFor = (f) => `${window.location.origin}/order/${f.token}`;
const fmtD = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function FormEditor({ form, priceLists, onSaved, onCancel }) {
  const [f, setF] = useState({
    title: form?.title || '', intro: form?.intro || '', price_list_id: form?.price_list_id || '',
    show_prices: form?.show_prices ?? true, allow_collection: form?.allow_collection ?? true, allow_delivery: form?.allow_delivery ?? true,
    closes_on: form?.closes_on ? String(form.closes_on).slice(0, 10) : '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  async function save(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const r = await apiFetch(form ? `/orders/order-forms/${form.id}` : '/orders/order-forms', { method: form ? 'PATCH' : 'POST', body: { ...f, price_list_id: f.price_list_id || null, closes_on: f.closes_on || null } });
      onSaved(r.data ?? r);
    } catch (ex) { setErr(ex.message); } finally { setSaving(false); }
  }
  return (
    <form onSubmit={save} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16, display: 'grid', gap: 12 }}>
      <strong style={{ fontSize: 15 }}>{form ? 'Change the order form' : 'New order form'}</strong>
      <label style={label}>Title<input style={input} value={f.title} onChange={set('title')} placeholder="e.g. Christmas orders 2026" required /></label>
      <label style={label}>A few words at the top <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
        <textarea style={{ ...input, minHeight: 70, resize: 'vertical' }} value={f.intro} onChange={set('intro')} placeholder="e.g. Order by 12 December for delivery before Christmas. We'll be in touch to confirm and take payment." />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <label style={label}>Products and prices from
          <select style={input} value={f.price_list_id} onChange={set('price_list_id')}>
            <option value="">All our bottled products (no prices)</option>
            {priceLists.map((p) => <option key={p.id} value={p.id}>{p.name} price list</option>)}
          </select>
        </label>
        <label style={label}>Closes on <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
          <input type="date" style={input} value={f.closes_on} onChange={set('closes_on')} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13 }}>
        {f.price_list_id && <label><input type="checkbox" checked={f.show_prices} onChange={set('show_prices')} /> Show prices</label>}
        <label><input type="checkbox" checked={f.allow_delivery} onChange={set('allow_delivery')} /> Can be delivered</label>
        <label><input type="checkbox" checked={f.allow_collection} onChange={set('allow_collection')} /> Can be collected</label>
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 13 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" style={btn(false)} onClick={onCancel}>Cancel</button>
        <button type="submit" style={btn(true)} disabled={saving}>{saving ? 'Saving…' : form ? 'Save' : 'Make the form'}</button>
      </div>
    </form>
  );
}

export function OrderFormsView({ onOpenInbox }) {
  const { data, refetch } = useApi('/orders/order-forms');
  const { data: plData } = useApi('/price-lists');
  const forms = Array.isArray(data) ? data : [];
  const priceLists = (plData?.price_lists ?? plData?.data ?? (Array.isArray(plData) ? plData : [])).filter((p) => p.status !== 'archived');
  const [editing, setEditing] = useState(null); // 'new' | form
  const [copied, setCopied] = useState(null);

  async function copy(f) {
    try { await navigator.clipboard.writeText(linkFor(f)); setCopied(f.id); setTimeout(() => setCopied(null), 1800); } catch { await uiPrompt('Copy this link and send it:', linkFor(f), { title: 'Order form link' }); }
  }
  async function toggle(f) { await apiFetch(`/orders/order-forms/${f.id}`, { method: 'PATCH', body: { active: !f.active } }); refetch(); }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)', maxWidth: 620 }}>
          Make a form, copy its link and send it. Anyone with the link can order — what they send comes into the <button type="button" onClick={onOpenInbox} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 13.5 }}>Email inbox</button> to review and confirm, like an emailed order.
        </p>
        {!editing && <button type="button" style={btn(true)} onClick={() => setEditing('new')}>+ New order form</button>}
      </div>

      {editing && <FormEditor form={editing === 'new' ? null : editing} priceLists={priceLists} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}

      {forms.length === 0 && !editing && <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No order forms yet.</p>}
      {forms.map((f) => {
        const closed = f.closes_on && String(f.closes_on).slice(0, 10) < new Date().toISOString().slice(0, 10);
        return (
          <div key={f.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10, opacity: f.active && !closed ? 1 : 0.65 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <strong style={{ fontSize: 15 }}>{f.title}</strong>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                  {f.price_list_name ? `${f.price_list_name} price list${f.show_prices ? '' : ' (prices hidden)'}` : 'All bottled products, no prices'}
                  {f.closes_on ? ` · ${closed ? 'closed' : 'closes'} ${fmtD(f.closes_on)}` : ''}
                  {` · ${f.responses} order${f.responses === 1 ? '' : 's'} in`}
                  {!f.active ? ' · switched off' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button type="button" style={btn(true)} onClick={() => copy(f)}>{copied === f.id ? '✓ Link copied' : 'Copy link'}</button>
                <a href={linkFor(f)} target="_blank" rel="noreferrer" style={{ ...btn(false), textDecoration: 'none' }}>Open</a>
                <button type="button" style={btn(false)} onClick={() => setEditing(f)}>Change</button>
                <button type="button" style={btn(false)} onClick={() => toggle(f)}>{f.active ? 'Switch off' : 'Switch on'}</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
